import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AccessDeniedComponent } from '@common/access-denied/access-denied.component';
import { FullComponent } from '@layouts/full/full.component';

import { afterLoginGuard } from '@guards/after-login.guard';
import { beforeLoginGuard } from '@guards/before-login.guard';
import { projectVisibilityGuard } from '@guards/project-visibility.guard';
import { AddRoleComponent } from './modules/setting/manage-user-contacts/roles/add-role/add-role.component';
import { FlowMainComponent } from './modules/workflow/flow-main/flow-main.component';
import { LeadBookingPublicComponent } from './public-page/lead-booking-public/lead-booking-public.component';
import { ListingPreviewStaticComponent } from './public-page/listing-preview-static/listing-preview-static.component';

const routes: Routes = [
  {
    path: '',
    component: FullComponent,
    canActivate: [afterLoginGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./modules/dash-board/dash-board.module').then(m => m.DashBoardModule),
      },
      {
        path: 'dashboard-new',
        loadChildren: () => import('./modules/dashboard-new/dashboard-new.module').then(m => m.DashboardNewModule),
      },
      {
        path: 'setting/workflow/flow',
        component: FlowMainComponent,
        data: {
          title: 'Flow',
          breadcrumb: [
            { label: 'Home', url: '/' },
            { label: 'Automation', url: '' },
          ],
        },
      },
      {
        path: 'add-role',
        component: AddRoleComponent,
        data: {
          title: 'Add Team',
          permissions: {
            only: 'team.manage',
          },
        },
      },
      {
        path: 'files',
        loadChildren: () => import('./modules/explorer/explorer.module').then(m => m.ExplorerModule),
      },
      {
        path: 'access-denied',
        component: AccessDeniedComponent,
      },
      {
        path: 'project_',
        loadChildren: () => import('./modules/projects/main-project.module').then(m => m.MainProjectModule),
      },
      {
        path: 'email-inbox',
        loadChildren: () => import('./modules/my-inbox/my-inbox.module').then(m => m.MyInboxModule),
      },
      {
        path: 'my-inbox',
        loadChildren: () => import('./modules/email-inbox/email-inbox.module').then(m => m.EmailInboxModule),
      },
      {
        path: 'calender',
        loadChildren: () => import('./modules/email-calender/email-calender.module').then(m => m.EmailCalenderModule),
      },
      {
        path: 'my-calander',
        loadChildren: () => import('./modules/my-calander/my-calander.module').then(m => m.MyCalanderModule),
      },
      {
        path: 'projects/:projectId/calendar',
        canActivate: [projectVisibilityGuard],
        loadChildren: () => import('./modules/my-calander/my-calander.module').then(m => m.MyCalanderModule),
      },
      // library
      {
        path: 'library',
        loadChildren: () => import('./modules/library/library.module').then(m => m.LibraryModule),
      },
      // library
      {
        path: 'my-whiteboard',
        loadChildren: () => import('./modules/live-drawing-editor/live-drawing-editor.module').then(m => m.LiveDrawingEditorModule),
      },
      {
        path: 'staff-contact',
        loadChildren: () => import('./modules/view-user-staff/view-user-staff.module').then(m => m.ViewUserStaffModule),
      },
      {
        path: 'setting',
        loadChildren: () => import('./modules/setting/settings.module').then(m => m.SettingsModule),
      },
      {
        // The Dynamic sync-failure notification links to `/settings/…` (backend's
        // DYNAMIC_SYNC_FAILED_PAGE_LINK) while every settings route here lives under
        // `/setting/…`. Alias it so the notification click never 404s; query params
        // (`?lead_id=`) are carried through the redirect.
        path: 'settings/failed-dynamic-sync',
        redirectTo: 'setting/failed-dynamic-sync',
      },
      // my tasks
      {
        path: 'tasks',
        loadChildren: () => import('./modules/my-tasks/my-tasks.module').then(m => m.MyTasksModule),
      },
      {
        path: 'contracts',
        loadChildren: () => import('./modules/contract-agreements/contract-agreements.module').then(m => m.ContractAgreementsModule),
      },
      {
        path: 'projects',
        loadChildren: () => import('./modules/my-projects/my-projects.module').then(m => m.MyProjectsModule),
      },
      {
        path: 'setting/profile',
        loadChildren: () => import('./modules/my-profile/my-profile.module').then(m => m.MyProfileModule),
      },
      {
        path: 'listings',
        loadChildren: () => import('./modules/my-listings/my-listings.module').then(m => m.MyListingsModule),
      },
      {
        path: 'property',
        loadChildren: () => import('./modules/my-listings/properties/my-property.module').then(m => m.MyListingsModule),
      },
      {
        path: 'project',
        loadChildren: () => import('./modules/my-listings/listing-projects/listing-project.module').then(m => m.ListingProjectModule),
      },
      {
        path: 'leads',
        loadChildren: () => import('./modules/leads-management/leads-management.module').then(m => m.LeadsManagementModule),
      },
      {
        path: '',
        loadChildren: () => import('./modules/reports/reporting.module').then(m => m.ReportsModule),
      },
      {
        path: 'ofi',
        loadChildren: () => import('./modules/open-for-inspection/open-for-inspection.module').then(m => m.OpenForInspectionModule),
      },
      {
        path: 'global',
        loadChildren: () => import('./modules/global-search/global-search.module').then(m => m.GlobalSearchModule),
      },
      {
        path: 'personal-notes',
        loadChildren: () => import('./public-page/personal-notes/notes.module').then(m => m.NotesModule),
      },
      {
        path: 'sale-presentation',
        loadChildren: () => import('./modules/sale-presentation/sale-presentation.module').then(m => m.SalePresentationModule),
      },
      {
        // Internal office chat. Standalone component, so it loads on its own rather than
        // dragging a module along. Backend contract: docs/backend/chat/internal-chat.md.
        path: 'chat',
        loadComponent: () => import('./modules/chat/components/chat-page/chat-page.component').then(m => m.ChatPageComponent),
        data: {
          title: 'Chat',
        },
      },
    ],
  },
  {
    path: 'listing-preview/:id',
    component: ListingPreviewStaticComponent,
  },
  {
    path: 'public/lead-booking',
    component: LeadBookingPublicComponent,
  },
  {
    // Agent-facing preview of an unsaved External EOI configuration, opened in a new tab by the
    // "Preview" button on the project's External EOI tab. Reads its payload from sessionStorage
    // (written by the opener just before `window.open`) rather than a token, since there is no
    // real link yet. Matched before `external-eoi/:token` below so `:token` doesn't swallow it.
    path: 'external-eoi/preview',
    loadComponent: () => import('./public-page/external-eoi-form/external-eoi-form.component').then(m => m.ExternalEoiFormComponent),
    data: { preview: true },
  },
  {
    // Public, unauthenticated buyer-facing External EOI form.
    // Kept for every link minted before the readable URL below existed — those
    // tokens must never stop working.
    path: 'external-eoi/:token',
    loadComponent: () => import('./public-page/external-eoi-form/external-eoi-form.component').then(m => m.ExternalEoiFormComponent),
  },
  // ===== REFERRAL TRACKING DEMO (frontend-only) — delete this block + the
  // `src/app/modules/referral-demo` folder to remove the feature entirely =====
  {
    path: 'register-lead',
    loadComponent: () => import('./modules/referral-demo/register-demo/register-demo.component').then(m => m.RegisterDemoComponent),
  },
  // ===== END REFERRAL TRACKING DEMO =====
  // Public, unauthenticated buyer-facing booking pages. Each carries the signed `?t=` token
  // minted by `GET /calendar/booking-link` — the endpoints reject a hand-built link.
  // Literal-prefixed paths come first so `manage`/`u` aren't swallowed by `:projectSlug`.
  {
    path: 'book/manage/:manageToken',
    loadComponent: () =>
      import('./modules/my-calander/calendar/components/calendar-booking-manage/calendar-booking-manage.component').then(
        m => m.CalendarBookingManageComponent
      ),
  },
  {
    // Personal calendar: no project, so the link keys off the agent's `booking_slug`.
    path: 'book/u/:bookingSlug/:typeSlug',
    loadComponent: () =>
      import('./modules/my-calander/calendar/components/calendar-public-view/calendar-public-view.component').then(
        m => m.CalendarPublicViewComponent
      ),
  },
  {
    path: 'book/u/:bookingSlug',
    loadComponent: () =>
      import('./modules/my-calander/calendar/components/calendar-public-view/calendar-public-view.component').then(
        m => m.CalendarPublicViewComponent
      ),
  },
  {
    path: 'book/:projectSlug/:typeSlug',
    loadComponent: () =>
      import('./modules/my-calander/calendar/components/calendar-public-view/calendar-public-view.component').then(
        m => m.CalendarPublicViewComponent
      ),
  },
  {
    path: 'book/:projectSlug',
    loadComponent: () =>
      import('./modules/my-calander/calendar/components/calendar-public-view/calendar-public-view.component').then(
        m => m.CalendarPublicViewComponent
      ),
  },
  {
    // Readable buyer-facing External EOI URL:
    //   portal-remmi.com.au/{project}/eoi/{lot}/{client}
    // The literal `eoi` in the second segment is what keeps this from
    // swallowing other paths, so it is matched last — after every named public
    // route above — and only ever with all four segments present.
    path: ':projectSlug/eoi/:lotSlug/:clientSlug',
    loadComponent: () => import('./public-page/external-eoi-form/external-eoi-form.component').then(m => m.ExternalEoiFormComponent),
  },
  {
    path: '',
    canActivate: [beforeLoginGuard],
    loadChildren: () => import('./modules/Auth/login.module').then(m => m.LoginModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
